/* global axios */
// DahReply — all calls proxied through Rails backend.
// KNOWLEDGE_API_KEY injected server-side; no secret in the browser bundle.
//
// NOTE: VUE_APP_DIFY_API_URL and VUE_APP_DIFY_API_KEY were declared here but
// never used in any action — they were dead variables and have been removed.
// All actions call the knowledge API, not a separate Dify endpoint.

const knowledgeProxy = (accountId, path) =>
  `/api/v1/accounts/${accountId}/proxy/knowledge${path}`;

export const state = {
  currentPage: '1',
  currentLimit: '10',
  keyword: '',
  enabled: 'all',
  chunkList: [],
  totalChunks: 0,
  totalPages: 0,
  hasMore: false,
  documentDetails: {},
};

export const getters = {
  paginationInfo: state => ({
    currentPage: parseInt(state.currentPage, 10),
    totalPages: state.totalPages,
    hasMore: state.hasMore,
  }),
  filteredChunks: state => state.chunkList,
};

export const mutations = {
  updateChunkList(state, update) {
    state.chunkList = update;
  },
  updateDocumentDetails(state, update) {
    state.documentDetails = update;
  },
  updatePaginationInfo(state, { total, totalPages, hasMore }) {
    state.totalChunks = total;
    state.totalPages = totalPages;
    state.hasMore = hasMore;
  },
  setCurrentPage(state, page) {
    state.currentPage = page.toString();
  },
  setCurrentLimit(state, limit) {
    state.currentLimit = limit.toString();
  },
  setKeyword(state, keyword) {
    state.keyword = keyword;
  },
  setEnabled(state, enabled) {
    state.enabled = enabled;
  },
  resetChunkState(state) {
    state.chunkList = [];
    state.currentPage = '1';
    state.currentLimit = '10';
    state.keyword = '';
    state.enabled = 'all';
    state.totalChunks = 0;
    state.totalPages = 0;
    state.hasMore = false;
    state.documentDetails = {};
  },
};

export const actions = {
  async fetchChunks({ state, commit, rootGetters }, { datasetId, documentId }) {
    const accountId = rootGetters.getCurrentAccountId;
    const params = {
      page: state.currentPage || 1,
      limit: state.currentLimit || 10,
      keyword: state.keyword || '',
      enabled: state.enabled || 'all',
    };

    try {
      const { data: response } = await axios.get(
        knowledgeProxy(accountId, `/chatbot/datasets/${datasetId}/documents/${documentId}/segments`),
        { params }
      );

      commit('updateChunkList', response.data);

      const totalItems = response.total || 0;
      const totalPages = Math.ceil(totalItems / parseInt(state.currentLimit, 10));
      const hasMore = parseInt(state.currentPage, 10) < totalPages;

      commit('updatePaginationInfo', { total: totalItems, totalPages, hasMore });

      return response;
    } catch (err) {
      throw new Error('Error fetching chunks');
    }
  },

  async updateChunk({ rootGetters }, { datasetId, documentId, segmentId, payload }) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      await axios.patch(
        knowledgeProxy(accountId, `/chatbot/datasets/${datasetId}/documents/${documentId}/segments/${segmentId}`),
        payload
      );
    } catch (err) {
      throw new Error('Error updating chunk');
    }
  },

  async enableChunk({ rootGetters }, { datasetId, documentId, ids }) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      for (const segmentId of ids) {
        await axios.patch(
          knowledgeProxy(accountId, `/chatbot/datasets/${datasetId}/documents/${documentId}/segments/${segmentId}/enable`)
        );
      }
    } catch (err) {
      throw new Error('Error enabling chunk');
    }
  },

  async disableChunk({ rootGetters }, { datasetId, documentId, ids }) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      for (const segmentId of ids) {
        await axios.patch(
          knowledgeProxy(accountId, `/chatbot/datasets/${datasetId}/documents/${documentId}/segments/${segmentId}/disable`)
        );
      }
    } catch (err) {
      throw new Error('Error disabling chunk');
    }
  },

  async deleteChunk({ rootGetters }, { datasetId, documentId, ids }) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      for (const segmentId of ids) {
        await axios.delete(
          knowledgeProxy(accountId, `/chatbot/datasets/${datasetId}/documents/${documentId}/segments/${segmentId}`)
        );
      }
    } catch (err) {
      throw new Error('Error deleting chunk');
    }
  },

  async createChunk({ rootGetters }, { datasetId, documentId, payload }) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      await axios.post(
        knowledgeProxy(accountId, `/chatbot/datasets/${datasetId}/documents/${documentId}/segments`),
        payload
      );
    } catch (err) {
      throw new Error('Error creating chunk');
    }
  },

  async toggleDocument({ rootGetters }, { datasetId, documentId, status }) {
    const accountId = rootGetters.getCurrentAccountId;
    const endpoint = status === 'enable' ? 'enable' : 'disable';
    try {
      await axios.patch(
        knowledgeProxy(accountId, `/chatbot/datasets/${datasetId}/documents/${documentId}/${endpoint}`)
      );
    } catch (err) {
      console.error('Error toggling document:', err);
    }
  },

  async getDocumentDetails({ commit, rootGetters }, { datasetId, documentId }) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const { data } = await axios.get(
        knowledgeProxy(accountId, `/chatbot/datasets/${datasetId}/documents/${documentId}/details`),
        { params: { metadata: 'without', account_id: accountId } }
      );
      commit('updateDocumentDetails', data);
      return data;
    } catch (err) {
      throw new Error('Error fetching document');
    }
  },

  async renameDocument({ rootGetters }, { datasetId, documentId, payload }) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      await axios.post(
        knowledgeProxy(accountId, `/chatbot/datasets/${datasetId}/documents/${documentId}/rename`),
        payload
      );
    } catch (err) {
      throw new Error('Error renaming document');
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
